import { Component, h, RefObject } from 'preact';
import cx from 'classnames';

import { DivCat, PropertyType } from '../../../../../types';
import { Form } from '../../../../utils/forms/form';
import { cxValidateField } from '../../../../utils/forms/validators';
import {
  propertyTypes, Amenities, FurnishingChoices, PropertyInfo, ComOperationalHours,
  ComOperationalHoursOther, BedroomChoices, LeaseTerms
} from '../../../../../constants';

import { LabeledChoiceButtonGroup } from '../../common/labeled-choice-button-group.component';
import { ChoicesButtonGroup } from '../../common/choices-button-group.component';
import { FormattedNumberInput } from '../../common/formatted-number-input.component';
import { DatePickerControl } from '../../common/date-picker-control.component';
import { RadioGroup } from '../../common/radio-group.component';
import { CheckboxGroup } from '../../common/checkbox-group.component';
import { MultiLocationField } from '../../common/multi-location-field.component';

interface AddClientFastTrackFormProps {
  form: Form;
  inputRefs: { [key: string]: RefObject<HTMLInputElement> };
  onFieldChange(fieldName: string, value: any);
}

export class AddClientFastTrackForm extends Component<AddClientFastTrackFormProps> {
  createChangeHandler(fieldName: string) {
    return (event: Event) => {
      const target = event.target as HTMLInputElement;
      this.props.onFieldChange(fieldName, target.value);
    }
  }

  getDivCat() {
    const form = this.props.form;
    return {
      division: form.fields.division.value,
      category: form.fields.category.value,
      type: form.fields.type.value
    } as DivCat;
  }

  getPropertyTypeChoices() {
    const form = this.props.form;
    const selectedDivision = form.fields.division.value;
    
    return propertyTypes.filter(type => type.division === selectedDivision)
      .map(type => ({ label: type.label, value: type.label }));
  }

  getPropertyTypeRadioGroup(props: AddClientFastTrackFormProps) {
    const form = props.form;

    // Commercial has some longer property type names, and needs more space.
    const colOffset = form.fields.division.value === 'Residential' ? 0 : 1;
    
    return (
      <RadioGroup choices={ this.getPropertyTypeChoices() }
        value={ form.fields.type.value }
        onSelect={ choice => props.onFieldChange('type', choice) }
        showInvalidState={ cxValidateField(form.fields.type) }
        ref={ props.inputRefs.type }
        
        layout={{ columns: 2 - colOffset }}
        layoutLg={{ columns: 4 - colOffset }} />
    );
  }

  isNonLotResidential() {
    const form = this.props.form;
    const residential = form.fields.division.value === 'Residential';
    const isVacantLot = form.fields.type.value === 'Vacant Lot';

    return residential && !isVacantLot;
  }

  isCommercial() {
    const form = this.props.form;
    const commercial = form.fields.division.value === 'Commercial';

    return commercial;
  }

  isRent() {
    const divCat = this.getDivCat();
    return divCat.category === 'Rent';
  }

  isOperationalHoursOtherSelected() {
    const hours = this.props.form.fields.operationalHours.value;
    return hours === ComOperationalHoursOther.value;
  }

  doesTypeHaveBedrooms() {
    const form = this.props.form;
    const type = form.fields.type.value as PropertyType;

    return type === 'Condominium' || type === 'House and Lot' || type === 'Townhouse';
  }

  getBedroomChoices() {
    const type = this.props.form.fields.type.value as PropertyType;
    if (type === 'House and Lot' || type === 'Townhouse') {
      return BedroomChoices.filter(choice => choice.value > 0);
    } else {
      // This should not be called for non-residential or lot types, anyway
      return BedroomChoices;
    }
  }

  getExistingBusinessFields(props: AddClientFastTrackFormProps) {
    const form = props.form;

    if (form.fields.businessStatus.value === 1) {
      return (
        <div className="hi-form-grid hi-form-take-row">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Client company name</label> 
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientCompanyName)
              })}
              placeholder="Client's company name"
              value={ form.fields.clientCompanyName.value }
              onChange={ this.createChangeHandler('clientCompanyName') }
              ref={ props.inputRefs.clientCompanyName } />
          </div>
          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's building or compound name</label> 
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientBuildingName)
              })}
              placeholder="Client's building or compound name"
              value={ form.fields.clientBuildingName.value }
              onChange={ this.createChangeHandler('clientBuildingName') }
              ref={ props.inputRefs.clientBuildingName } />
          </div>
          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's current floor &amp; unit number</label> 
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientFloorUnit)
              })}
              value={ form.fields.clientFloorUnit.value }
              placeholder="Client's current floor and/or unit number"
              onChange={ this.createChangeHandler('clientFloorUnit') }
              ref={ props.inputRefs.clientFloorUnit } />
          </div>
          <div className="hi-input-group">
            <label className="hi-input-group__label">Current location of client's business</label> 
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientLocation)
              })}
              placeholder="Current location of client's business"
              value={ form.fields.clientLocation.value }
              onChange={ this.createChangeHandler('clientLocation') }
              ref={ props.inputRefs.clientLocation } />
          </div>
        </div>
      )
    }
  }

  render(props: AddClientFastTrackFormProps) {
    const form = props.form;

    return (
      <div className="hi-form-grid">
        <div className="hi-input-group">
          <label className="hi-input-group__label">Property Kind</label>
          <ChoicesButtonGroup choices={[ 'Residential', 'Commercial' ]}
            selectedChoice={ form.fields.division.value }
            valid={ !cxValidateField(form.fields.division) }
            onSelect={ choice => props.onFieldChange('division', choice) }
            ref={ props.inputRefs.division } />
        </div>

        <div className="hi-input-group">
          <label className="hi-input-group__label">Category</label>
          <LabeledChoiceButtonGroup
            choices={[
              { label: 'For rent', value: 'Rent' },
              { label: 'For sale', value: 'Sale' }
            ]}
            value={ form.fields.category.value }
            valid={ !cxValidateField(form.fields.category) }
            onSelect={ choice => props.onFieldChange('category', choice) }
            ref={ props.inputRefs.category } />
        </div>

        <div className="hi-input-group hi-form-take-row">
          <label className="hi-input-group__label">Property Type</label>
          { this.getPropertyTypeRadioGroup(props) }
        </div>

        { this.isRent() && (
          <div className="hi-form-take-row">
            <label className="hi-input-group__label">Lease Term (years)</label>
            <RadioGroup
              choices={ LeaseTerms }
              value={ form.fields.minLeaseTerms.value }
              onSelect={ choice => props.onFieldChange('minLeaseTerms', choice) }
              showInvalidState={ cxValidateField(form.fields.minLeaseTerms) }
              ref={ props.inputRefs.minLeaseTerms }
              
              layout={{ columns: 2 }}
              layoutLg={{ flex: true }}/>
          </div>
        ) }

        { this.doesTypeHaveBedrooms() && (  
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Bedrooms</label>
            <RadioGroup
              choices={ this.getBedroomChoices() }
              value={ form.fields.bedrooms.value }
              onSelect={ choice => props.onFieldChange('bedrooms', choice) }
              showInvalidState={ cxValidateField(form.fields.bedrooms) }
              ref={ props.inputRefs.bedrooms }
              
              layout={{ columns: 2 }}
              layoutLg={{ flex: true }} />
          </div>
        )}

        { this.isNonLotResidential() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Furnishing</label>
            <CheckboxGroup choices={ FurnishingChoices.map(choice => choice.label ) }
              selected={ form.fields.furnishing.value }
              onSelectionChange={ amenities => props.onFieldChange('furnishing', amenities) }
              showInvalidState={ cxValidateField(form.fields.furnishing) }
              ref={ props.inputRefs.furnishing }
              
              layoutLg={{ columns: 3 }} />
          </div>
        )}

        { this.isNonLotResidential() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Amenities</label>
            <CheckboxGroup
              choices={ Amenities.filter(a => a.label !== 'Parking Spaces').map(a => a.label) }
              selected={ form.fields.amenities.value }
              onSelectionChange={ selected => props.onFieldChange('amenities', selected) }
              showInvalidState={ cxValidateField(form.fields.amenities) }
              ref={ props.inputRefs.amenities }
              
              layout={{ columns: 2 }} 
              layoutLg={{ columns: 3 }} />
          </div>
        )}

        <div className="hi-input-group hi-form-take-row">
          <label className="hi-input-group__label">Location</label>
          <MultiLocationField
            divCat={ this.getDivCat() }
            locations={ form.fields.locations.value }
            showInvalidState={ cxValidateField(form.fields.locations) }
            onLocationsChange={ locations => props.onFieldChange('locations', locations) }
            ref={ props.inputRefs.locations } />
        </div>

        {/* COMMERCIAL FIELDS */}

        { this.isCommercial() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Days of Operations</label>
            <CheckboxGroup
              choices={[
                'Mondays',
                'Tuesdays',
                'Wednesdays',
                'Thursdays',
                'Fridays',
                'Saturdays',
                'Sundays'
              ]}
              selected={ form.fields.operationalDays.value }
              onSelectionChange={ choices => props.onFieldChange('operationalDays', choices) }
              showInvalidState={ cxValidateField( form.fields.operationalDays )}
              ref={ props.inputRefs.operationalDays }
              
              layout={{ columns: 2 }}
              layoutLg={{ columns: 4 }} />
          </div>
        )}

        { this.isCommercial() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Business Status</label>
            <RadioGroup
              choices={[
                { value: 1, label: 'Existing Business' },
                { value: 2, label: 'New Business' },
              ]}
              value={ form.fields.businessStatus.value }
              onSelect={ status => props.onFieldChange('businessStatus', status) }
              showInvalidState={ cxValidateField( form.fields.businessStatus )}
              ref={ props.inputRefs.businessStatus }
              
              layout={{ columns: 1 }}
              layoutLg={{ flex: true }}
            />
          </div>
        )}

        { this.getExistingBusinessFields(props) }

        { this.isCommercial() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Nature of Business</label>
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.natureOfBusiness)
              }) }
              placeholder='Nature of business'
              value={ form.fields.natureOfBusiness.value }
              onChange={ this.createChangeHandler('natureOfBusiness') }
              ref={ props.inputRefs.natureOfBusiness } />
          </div>
        )}

        { this.isCommercial() && (
          <div className="hi-input-group hi-form-take-row">
            <label className="hi-input-group__label">Operational Hours</label>
            <RadioGroup choices={ ComOperationalHours }
              value={ form.fields.operationalHours.value }
              onSelect={ hours => props.onFieldChange('operationalHours', hours) }
              showInvalidState={ cxValidateField( form.fields.operationalHours )}
              ref={ props.inputRefs.operationalHours }
              
              layout={{ columns: 1 }}
              layoutLg={{ columns: 2 }} />
          </div>
        )}

        { this.isOperationalHoursOtherSelected() && (
          <div className="hi-input-group">
            <label className="hi-input-group__label">Operational Hours, Other</label>
            <input type="text"
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.operationalOthers)
              }) }
              value={ form.fields.operationalOthers.value }
              onChange={ this.createChangeHandler('operationalOthers') }
              ref={ props.inputRefs.operationalOthers } />
          </div>
        ) }

        { this.isCommercial() && (
          <div className="hi-input-group">
            <label className="hi-input-group__label">Building Class</label>
            <select
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.buildingClass)
              }) }
              value={ form.fields.buildingClass.value }
              onChange={ this.createChangeHandler('buildingClass')}
              ref={ props.inputRefs.buildingClass }>
              <option>Premium</option>
              <option>Class A</option>
              <option>Class B</option>
              <option>Class C</option>
            </select>
          </div>
        )}

        {/* END COMMERCIAL FIELDS */}


        { PropertyInfo.requiresFloorArea.includes(form.fields.type.value) && (
          <div className="hi-input-group">
            <label className="hi-input-group__label">Floor Area (SQM)</label>
            <div className="d-flex">
              <FormattedNumberInput type="number" placeholder="Min"
                className={ cx('form-control hi-border-radius-right-0 w-50', {
                  'is-invalid': cxValidateField(form.fields.minFloorArea)
                }) }
                suffix=" sqm"
                value={ form.fields.minFloorArea.value }
                onChange={ this.createChangeHandler('minFloorArea') }
                ref={ props.inputRefs.minFloorArea } />
              <FormattedNumberInput type="number" placeholder="Max"
                className={ cx('form-control hi-border-radius-left-0 w-50', {
                  'is-invalid': cxValidateField(form.fields.maxFloorArea)
                }) }
                suffix=" sqm"
                value={ form.fields.maxFloorArea.value }
                onChange={ this.createChangeHandler('maxFloorArea') }
                ref={ props.inputRefs.maxFloorArea } />
            </div>
          </div>
        )}

        { PropertyInfo.requiresLotArea.includes(form.fields.type.value) && (
          <div className="hi-input-group">
            <label className="hi-input-group__label">Lot Area (SQM)</label>
            <div className="d-flex">
              <FormattedNumberInput type="number" placeholder="Min"
                className={ cx('form-control hi-border-radius-right-0 w-50', {
                  'is-invalid': cxValidateField(form.fields.minLotArea)
                }) }
                suffix=" sqm"
                value={ form.fields.minLotArea.value }
                onChange={ this.createChangeHandler('minLotArea') }
                ref={ props.inputRefs.minLotArea } />
              <FormattedNumberInput type="number" placeholder="Max"
                className={ cx('form-control hi-border-radius-left-0 w-50', {
                  'is-invalid': cxValidateField(form.fields.maxLotArea)
                }) }
                suffix=" sqm"
                value={ form.fields.maxLotArea.value }
                onChange={ this.createChangeHandler('maxLotArea') }
                ref={ props.inputRefs.maxLotArea } />
            </div>
          </div>
        )}

        <div className="hi-input-group">
          <label className="hi-input-group__label">Price Range (PHP)</label>
          <div className="d-flex">
            <FormattedNumberInput type="number" placeholder="Min"
              className={ cx('form-control hi-border-radius-right-0 w-50', {
                'is-invalid': cxValidateField(form.fields.minPrice)
              }) }
              prefix="PHP "
              value={ form.fields.minPrice.value }
              onChange={ this.createChangeHandler('minPrice') }
              ref={ props.inputRefs.minPrice } />
            <FormattedNumberInput type="number" placeholder="Max"
              className={ cx('form-control hi-border-radius-left-0 w-50', {
                'is-invalid': cxValidateField(form.fields.maxPrice)
              }) }
              prefix="PHP "
              value={ form.fields.maxPrice.value }
              onChange={ this.createChangeHandler('maxPrice') }
              ref={ props.inputRefs.maxPrice } />
          </div>
        </div>

        <div className="hi-form-take-row hi-form-grid">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Preferred Viewing Date</label>
            <DatePickerControl
              className={ cx('form-control', { 'is-invalid': cxValidateField(form.fields.viewingDate) }) }
              value={ form.fields.viewingDate.value }
              onChange={ this.createChangeHandler('viewingDate') }
              ref={ props.inputRefs.viewingDate } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Target Move-In Date</label>
            <DatePickerControl
              className={ cx('form-control', { 'is-invalid': cxValidateField(form.fields.moveInDate) }) }
              value={ form.fields.moveInDate.value }
              onChange={ this.createChangeHandler('moveInDate') }
              ref={ props.inputRefs.moveInDate } />
          </div>
        </div>
      </div>
    );
  }
}
